/**
 * A automação de modelo pronto que não abre no mapa (a rotina agendada e o
 * lembrete): a configuração dela, para ler, e o "Editar configuração", que abre
 * o mesmo painel do "Usar" com o que está salvo.
 *
 * Mora na área das automações, ao lado do painel do modelo, e a casca a entrega
 * ao editor do mapa no lugar do "não tem mapa".
 */

import { useState, type ReactNode } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import type { AutomacaoDaApi } from '@cliqcrm/contracts';
import { mexeNoArDaAutomacao, montaOTipoDaAutomacao } from '@/entities/acesso/papel';
import { chaves } from '@/shared/api/chaves';
import {
  useApi,
  useBlocosDoEditor,
  useModelosDeAutomacao,
  useOperacoesDeDados,
} from '@/shared/api/consultas';
import { recadoDeErro } from '@/shared/api/recado-de-erro';
import { textoFixo, valorFixo } from '@/shared/lib/valor-fixo';
import { useMe } from '@/shared/sessao/contexto';
import { Botao, Ficha, Icone, type LinhaFicha } from '@/shared/ui';
import { Faixa } from '@/shared/ui/estados/faixa';
import {
  DIAS_DA_SEMANA,
  FUSOS,
  LINHA_DA_ROTINA,
  MODELO_LEMBRETE,
  camposDaOportunidadeDaExpressao,
  contatoDaExpressao,
  oportunidadeDaExpressao,
  textoDaExpressao,
  type Modelo,
  type ParametroDaAcao,
} from '../lib/modelos';
import { ConfigurarModelo } from './configurar-modelo';
import './configuracao-do-modelo.css';

type Horario = { dia_da_semana: number | null; hora: number; minuto: number };

const doisDigitos = (n: number): string => String(n).padStart(2, '0');

/** "Todo dia, 07:00" ou "Segunda, 18:30". */
function horarioEmPalavras(h: Horario): string {
  const dia = h.dia_da_semana === null ? 'Todo dia' : (DIAS_DA_SEMANA[h.dia_da_semana] ?? '?');
  return `${dia}, ${doisDigitos(h.hora)}:${doisDigitos(h.minuto)}`;
}

/** As colunas que a expressão usa: sem a lista da consulta, o resumo lê com elas. */
function colunasDa(expressao: string): string[] {
  const linha = new RegExp(`${LINHA_DA_ROTINA}\\.(?:\`([^\`]+)\`|([A-Za-z_]\\w*))`, 'g');
  return [...expressao.matchAll(linha)].map((m) => m[1] ?? m[2]!);
}

/** Um dado da ação como a pessoa o escolheu: a opção pelo nome, a coluna, o texto com `{coluna}`. */
function dadoEmPalavras(p: ParametroDaAcao | undefined, valor: string): ReactNode {
  const colunas = colunasDa(valor);
  const opcao = p?.opcoes?.find((o) => o.valor === textoFixo(valor));
  if (opcao) return opcao.rotulo;
  if (p?.opcao_nula && valorFixo(valor) === null) return p.opcao_nula;
  const contato = contatoDaExpressao(valor, colunas);
  if (p?.tipo === 'contato' && contato) {
    return `A coluna “${contato.coluna}” (${contato.como === 'telefone' ? 'telefone' : contato.como.toUpperCase()})`;
  }
  const oportunidade = oportunidadeDaExpressao(valor, colunas);
  if (p?.tipo === 'oportunidade' && oportunidade) {
    return `A coluna “${oportunidade.coluna}” (${oportunidade.como === 'referencia' ? 'a referência' : 'o id'})`;
  }
  const campos = camposDaOportunidadeDaExpressao(valor, colunas);
  if (p?.tipo === 'campos' && campos) {
    return [
      campos.nome && `Nome: ${campos.nome}`,
      campos.nomeDoContato && `contato novo: ${campos.nomeDoContato}`,
    ]
      .filter(Boolean)
      .join('; ');
  }
  const texto = textoDaExpressao(valor, colunas);
  if (texto !== null) return texto;
  return <code className="configuracao-do-modelo__expressao">{valor}</code>;
}

export function ConfiguracaoDoModelo({ automacao }: { automacao: AutomacaoDaApi }): ReactNode {
  const api = useApi();
  const cache = useQueryClient();
  const quem = useMe();
  const blocos = useBlocosDoEditor();
  const modelos = useModelosDeAutomacao();
  const [editando, definirEditando] = useState(false);
  const [salvo, definirSalvo] = useState<string | null>(null);

  // A versão que se lê e se edita: a em teste, quando há; senão, a de agora.
  const vista = automacao.pendente ?? automacao;
  const doModelo = vista.configuracao_do_modelo;
  const montaOTipo = montaOTipoDaAutomacao(quem, automacao.usa_sql);
  const podeEditar = montaOTipo && automacao.principal && automacao.situacao !== 'arquivada';
  // A mesma régua do editor do mapa: quem ativa, e a Cliqfy só as com SQL.
  const podeAtivar = mexeNoArDaAutomacao(quem, 'ativar', automacao.usa_sql);
  // A lista das consultas só para quem monta a com SQL: para os outros, ela é 404.
  const consultas = useOperacoesDeDados('consulta', automacao.usa_sql && montaOTipo);

  const ativar = useMutation({
    mutationFn: () => api!.ativarAutomacao({ caminho: { id: automacao.id } }),
    onSuccess: (resposta) => {
      cache.setQueryData(chaves.automacao(automacao.id), resposta);
      void cache.invalidateQueries({ queryKey: chaves.automacoes() });
      void cache.invalidateQueries({ queryKey: chaves.historicoDaAutomacao(automacao.id) });
      definirSalvo(`A versão ${resposta.versao} está no ar. Ela roda a partir de agora.`);
    },
  });

  if (!doModelo) return null;
  const c = doModelo.configuracao;
  const modelo: Modelo = modelos.data?.itens.find((m) => m.chave === doModelo.modelo) ?? {
    chave: doModelo.modelo,
    nome: automacao.nome ?? 'Modelo pronto',
    descricao: '',
    origem: '',
    usa_sql: automacao.usa_sql,
    disponivel: true,
  };
  const agenda = (c['agenda'] ?? { fuso: '', horarios: [] }) as {
    fuso: string;
    horarios: Horario[];
  };
  const operacaoId = typeof c['operacao_id'] === 'string' ? c['operacao_id'] : '';
  const operacao = consultas.data?.itens.find((o) => o.id === operacaoId);
  const acao = (c['acao'] ?? null) as { acao: string; parametros: Record<string, string> } | null;
  const daAcao = blocos.data?.acoes.find((a) => a.acao === acao?.acao);

  const linhas: LinhaFicha[] = [
    {
      rotulo: 'Quando roda',
      valor: (
        <ul className="configuracao-do-modelo__horarios">
          {agenda.horarios.map((h) => (
            <li key={horarioEmPalavras(h)}>{horarioEmPalavras(h)}</li>
          ))}
        </ul>
      ),
    },
    {
      rotulo: 'Fuso',
      valor: FUSOS.find((f) => f.valor === agenda.fuso)?.rotulo ?? agenda.fuso,
    },
    {
      rotulo: doModelo.modelo === MODELO_LEMBRETE ? 'Os compromissos' : 'A consulta',
      valor: operacao?.nome ?? (montaOTipo ? 'Consulta não encontrada' : 'Configurada pela Cliqfy'),
      vazio: !operacao,
    },
  ];
  if (doModelo.modelo === MODELO_LEMBRETE) {
    linhas.push({ rotulo: 'Modelo da mensagem', valor: String(c['modelo_do_lembrete'] ?? '') });
  }
  if (acao) {
    linhas.push({ rotulo: 'Com cada linha', valor: daAcao?.titulo ?? acao.acao });
    for (const [nome, valor] of Object.entries(acao.parametros)) {
      const p = daAcao?.parametros.find((x) => x.nome === nome);
      linhas.push({ rotulo: p?.titulo ?? nome, valor: dadoEmPalavras(p, valor) });
    }
  }

  return (
    <section className="configuracao-do-modelo" aria-label="Configuração da automação">
      <div className="configuracao-do-modelo__cabeca">
        <div>
          <h2 className="configuracao-do-modelo__titulo">{modelo.nome}</h2>
          <p className="configuracao-do-modelo__apoio">
            {automacao.pendente
              ? `A versão ${automacao.pendente.versao}, em teste, é a que aparece e se edita aqui.`
              : 'Feita de um modelo pronto: ela se configura por estes campos, sem mapa.'}
          </p>
        </div>
        {podeEditar && (
          <Botao
            variante="primario"
            onClick={() => {
              definirSalvo(null);
              definirEditando(true);
            }}
          >
            <Icone nome="lapis" />
            Editar configuração
          </Botao>
        )}
      </div>

      {salvo && <Faixa tom="defasado">{salvo}</Faixa>}
      {automacao.pendente && (
        <Faixa
          tom="atencao"
          {...(podeAtivar
            ? {
                acao: {
                  rotulo: ativar.isPending
                    ? 'Ativando…'
                    : `Ativar a versão ${automacao.pendente.versao}`,
                  aoClicar: () => {
                    if (!ativar.isPending && api) ativar.mutate();
                  },
                },
              }
            : {})}
        >
          A versão {automacao.pendente.versao}, em teste, ainda não está no ar. A versão{' '}
          {automacao.versao} continua rodando até alguém ativar a nova.
        </Faixa>
      )}
      {!automacao.pendente && automacao.situacao === 'rascunho' && (
        <Faixa tom="atencao">
          Em rascunho: ela não roda. Para pôr no ar, mande para teste e ative pela lista de
          automações.
        </Faixa>
      )}
      {ativar.isError && (
        <Faixa tom="bloqueado">
          {recadoDeErro(ativar.error, {
            generica: 'Não foi possível ativar agora. A versão de antes continua no ar.',
          })}
        </Faixa>
      )}

      <Ficha linhas={linhas} larguraRotulo="160px" />

      {editando && (
        <ConfigurarModelo
          modelo={modelo}
          edicao={automacao}
          aoFechar={() => definirEditando(false)}
          aoSalvo={(resposta) => {
            definirEditando(false);
            const nova = resposta.pendente?.versao ?? resposta.versao;
            definirSalvo(
              resposta.situacao === 'ativa' && resposta.pendente
                ? `Salvo como versão ${nova}, em teste. A versão ${resposta.versao} continua rodando até alguém ativar a nova.`
                : `Salvo como versão ${nova}.`,
            );
          }}
        />
      )}
    </section>
  );
}
